/**
 * TemplatesPanel — E3 · panel de plantillas: grilla, filtros, detalle y uso.
 * Al pasar el mouse por una tarjeta se reproduce sólo el fragmento de muestra
 * del MP4 (preview_start → preview_end), sin cortar el archivo.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  X, Loader2, LayoutTemplate, Search, Copy, Trash2, Pencil, Play,
  Replace, Plus, Film, AlertTriangle, Image,
} from 'lucide-react';
import { toast } from 'sonner';
import { templateService, grabFrameFromVideo } from '../../services/templateService';

const fmtDur = (s) => `${Math.round(Number(s) || 0)} s`;

const TemplateCard = ({ template, onUse, onSelect, selected }) => {
  const videoRef = useRef(null);

  const play = () => {
    const v = videoRef.current;
    if (!v || !template.preview_url) return;
    v.currentTime = template.preview_start || 0;
    v.play().catch(() => {});
  };
  const stop = () => {
    const v = videoRef.current;
    if (!v) return;
    v.pause();
    v.currentTime = template.preview_start || 0;
  };
  const onTime = () => {
    const v = videoRef.current;
    if (!v) return;
    const end = template.preview_end || (template.preview_start || 0) + 5;
    if (v.currentTime >= end) { v.currentTime = template.preview_start || 0; }
  };

  return (
    <div
      className={`tl-tpl-card ${selected ? 'selected' : ''}`}
      onMouseEnter={play}
      onMouseLeave={stop}
      onClick={() => onSelect(template)}
      data-testid={`tl-tpl-card-${template.id}`}
    >
      <div className="tl-tpl-thumb">
        {template.preview_url && (
          <video
            ref={videoRef} src={template.preview_url} muted playsInline
            poster={template.thumbnail_url || undefined}
            onTimeUpdate={onTime}
          />
        )}
        {!template.preview_url && (template.thumbnail_url
          ? <img src={template.thumbnail_url} alt={template.name} />
          : <div className="tl-tpl-thumb-empty"><Film size={22} /></div>)}
        {template.preview_url && <span className="tl-tpl-play"><Play size={12} /></span>}
      </div>
      <div className="tl-tpl-meta">
        <strong>{template.name}</strong>
        <small>
          {fmtDur(template.duration_seconds)} · {template.slot_count} hueco(s)
          {template.text_variable_count ? ` · ${template.text_variable_count} texto(s)` : ''}
        </small>
        {template.category && <em className="tl-tpl-chip">{template.category}</em>}
      </div>
      <button
        className="tl-tpl-use"
        onClick={(e) => { e.stopPropagation(); onUse(template, 'replace'); }}
        data-testid={`tl-tpl-use-${template.id}`}
      >
        <Replace size={11} /> Usar
      </button>
    </div>
  );
};

const TemplatesPanel = ({ projectId, onClose, onApplied, onOpenComplete }) => {
  const [scope, setScope] = useState('mine');
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('');
  const [state, setState] = useState(null);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      setState(await templateService.list({ scope, q, category }));
    } catch (e) {
      setState({ templates: [], error: true });
    }
  }, [scope, q, category]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const use = async (template, mode) => {
    if (!projectId) return toast.error('Abrí un proyecto para usar una plantilla');
    setBusy(template.id);
    try {
      const out = await templateService.instantiate(template.id, { projectId, mode });
      onApplied?.(out);
      toast.success(mode === 'replace'
        ? 'Plantilla aplicada al timeline'
        : 'Plantilla agregada al final del timeline');
      if (out.empty_slots?.length) onOpenComplete?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message
        || e.response?.data?.detail
        || 'No se pudo usar la plantilla');
    } finally { setBusy(''); }
  };

  const rename = async (template) => {
    const name = window.prompt('Nuevo nombre de la plantilla', template.name);
    if (!name) return;
    try {
      await templateService.patch(template.id, { name });
      toast.success('Plantilla renombrada');
      load();
    } catch (_e) { toast.error('No se pudo renombrar'); }
  };

  const duplicate = async (template) => {
    try {
      await templateService.duplicate(template.id);
      toast.success('Plantilla duplicada');
      load();
    } catch (_e) { toast.error('No se pudo duplicar'); }
  };

  const refreshThumb = async (template) => {
    if (!template.preview_url) return toast.error('Esta plantilla no tiene video de muestra');
    setBusy(template.id);
    try {
      const { dataUrl } = await grabFrameFromVideo(template.preview_url, 0.3);
      if (!dataUrl) {
        return toast.error('No se pudo leer el video para la miniatura (falta CORS en R2)');
      }
      const out = await templateService.setPreview(template.id, { thumbnail_data_url: dataUrl });
      (out.problems || []).forEach((p) => toast.warning(p));
      toast.success('Miniatura actualizada');
      load();
    } catch (_e) {
      toast.error('No se pudo actualizar la miniatura');
    } finally { setBusy(''); }
  };

  const remove = async (template) => {
    if (!window.confirm(`¿Eliminar "${template.name}"? Se puede volver a crear, pero esta copia se va.`)) return;
    try {
      await templateService.remove(template.id);
      toast.success('Plantilla eliminada');
      setSelected(null);
      load();
    } catch (_e) { toast.error('No se pudo eliminar'); }
  };

  return (
    <div className="tl-gallery-overlay" onClick={onClose} data-testid="tl-templates-overlay">
      <div className="tl-template-modal wide" onClick={(e) => e.stopPropagation()} data-testid="tl-templates-panel">
        <div className="tl-gallery-header">
          <span className="tl-gallery-title"><LayoutTemplate size={14} /> Plantillas</span>
          <div className="tl-gallery-tabs">
            <button
              className={`tl-gallery-tab ${scope === 'mine' ? 'active' : ''}`}
              onClick={() => setScope('mine')} data-testid="tl-tpl-tab-mine"
            >
              Mis plantillas
            </button>
            <button
              className={`tl-gallery-tab ${scope === 'platform' ? 'active' : ''}`}
              onClick={() => setScope('platform')} data-testid="tl-tpl-tab-platform"
            >
              De la plataforma
            </button>
          </div>
          <button className="tl-icon-btn" onClick={onClose} data-testid="tl-templates-close">
            <X size={14} />
          </button>
        </div>

        <div className="tl-tpl-filters">
          <span className="tl-tpl-search">
            <Search size={13} />
            <input
              type="text" placeholder="Buscar por nombre…"
              value={q} onChange={(e) => setQ(e.target.value)}
              data-testid="tl-tpl-search"
            />
          </span>
          {(state?.categories || []).length > 0 && (
            <select
              value={category} onChange={(e) => setCategory(e.target.value)}
              data-testid="tl-tpl-category"
            >
              <option value="">Todas las categorías</option>
              {state.categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
        </div>

        <div className="tl-tpl-body">
          {state === null && (
            <div className="tl-gallery-empty"><Loader2 size={18} className="spin" /></div>
          )}
          {state?.table_missing && (
            <div className="tl-template-warn" data-testid="tl-tpl-table-missing">
              <AlertTriangle size={12} />
              Faltan las tablas de plantillas: corré la migración <code>{state.migration}</code>.
            </div>
          )}
          {state && !state.table_missing && state.templates?.length === 0 && (
            <div className="tl-gallery-empty" data-testid="tl-tpl-empty">
              {scope === 'platform'
                ? 'Todavía no hay plantillas publicadas por la plataforma.'
                : 'No tenés plantillas. Armá un timeline y usá "Guardar como plantilla".'}
            </div>
          )}
          <div className="tl-tpl-grid">
            {(state?.templates || []).map((t) => (
              <TemplateCard
                key={t.id} template={t} selected={selected?.id === t.id}
                onUse={use} onSelect={setSelected}
              />
            ))}
          </div>
        </div>

        {selected && (
          <div className="tl-tpl-detail" data-testid={`tl-tpl-detail-${selected.id}`}>
            <div className="tl-tpl-detail-main">
              <strong>{selected.name}</strong>
              <small>
                {selected.description || 'Sin descripción'} · v{selected.version}
                {selected.preview_stale ? ' · video de muestra desactualizado' : ''}
              </small>
            </div>
            <button
              onClick={() => use(selected, 'replace')} disabled={busy === selected.id}
              data-testid="tl-tpl-detail-replace"
            >
              {busy === selected.id ? <Loader2 size={11} className="spin" /> : <Replace size={11} />}
              Reemplazar timeline
            </button>
            <button onClick={() => use(selected, 'append')} data-testid="tl-tpl-detail-append">
              <Plus size={11} /> Insertar al final
            </button>
            <button onClick={() => rename(selected)} data-testid="tl-tpl-detail-rename">
              <Pencil size={11} /> Renombrar
            </button>
            <button
              onClick={() => refreshThumb(selected)}
              data-testid="tl-tpl-detail-thumb"
            >
              <Image size={11} /> Miniatura
            </button>
            <button onClick={() => duplicate(selected)} data-testid="tl-tpl-detail-duplicate">
              <Copy size={11} /> Duplicar
            </button>
            <button className="danger" onClick={() => remove(selected)} data-testid="tl-tpl-detail-delete">
              <Trash2 size={11} /> Eliminar
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplatesPanel;
